import { Link, NavLink } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/store';
import { useLogout } from '@/features/auth/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { useDailyUsage } from '@/features/billing/hooks/useDailyUsage';
import { useUpgradeModalStore } from '@/features/billing/upgradeModalStore';
import { ThemeToggle } from '@/components/ui/ThemeToggle';

interface TopbarProps {
  children?: React.ReactNode;
}

// Layout: left = brand + primary nav (Dashboard, Projects);
// center = page-contextual children (editor prompt bar, etc.);
// right = plan state, account controls.
// The [body.paper_&]: overrides flip the topbar from dark to paper when
// the surrounding page uses the paper theme.
export function Topbar({ children }: TopbarProps) {
  const user = useAuthStore((s) => s.user);
  const { mutate: handleLogout, isPending } = useLogout();
  const { usage } = useDailyUsage();
  const openUpgrade = useUpgradeModalStore((s) => s.openModal);

  return (
    <header className="shrink-0 border-b border-line-subtle bg-clyra-base/80 backdrop-blur-md px-4 py-1.5 [body.paper_&]:border-stone-subtle [body.paper_&]:bg-paper/90">
      <div className="flex items-center gap-3">
        <Link
          to="/dashboard"
          className="shrink-0 flex items-center gap-2 hover:opacity-80 transition-opacity"
        >
          <span className="text-base font-semibold tracking-tight-ish text-ink-primary [body.paper_&]:text-charcoal">
            clyra<span className="text-brand-400 [body.paper_&]:text-teal-500">.</span>
          </span>
        </Link>

        {user && (
          <nav className="hidden shrink-0 items-center gap-1 pl-2 md:flex">
            <NavItem to="/dashboard">Dashboard</NavItem>
            <NavItem to="/projects">Projects</NavItem>
          </nav>
        )}

        <div className="flex min-w-0 flex-1 items-center gap-3 overflow-x-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">{children}</div>

        <div className="flex shrink-0 items-center gap-2">
          {user && (
            <>
              {usage && (
                <UsagePill
                  plan={user.plan}
                  used={usage.daily_used}
                  limit={usage.daily_limit}
                  onUpgrade={() => openUpgrade({ defaultPlan: 'pro' })}
                />
              )}
              {user.plan === 'free' && !usage && (
                <button
                  type="button"
                  onClick={() => openUpgrade({ defaultPlan: 'pro' })}
                  className="rounded-md border border-brand-500/40 bg-brand-500/10 px-2 py-0.5 text-tiny font-medium text-brand-300 hover:bg-brand-500/20 transition-colors [body.paper_&]:border-teal-500/40 [body.paper_&]:bg-teal-50 [body.paper_&]:text-teal-700 [body.paper_&]:hover:bg-teal-100"
                  title="Upgrade for higher daily limits"
                >
                  Upgrade
                </button>
              )}
              {user.role === 'admin' && (
                <Link
                  to="/admin"
                  className="text-tiny text-brand-500/70 hover:text-brand-400 transition-colors [body.paper_&]:text-teal-600 [body.paper_&]:hover:text-teal-500"
                >
                  Admin
                </Link>
              )}
              <ThemeToggle variant="compact" />
              <Link
                to="/settings"
                className="hidden text-tiny text-ink-muted hover:text-ink-primary transition-colors lg:inline-block [body.paper_&]:text-charcoal-muted [body.paper_&]:hover:text-charcoal"
                title="Settings, plan, AI keys"
              >
                Settings
              </Link>
              <span
                className="hidden truncate text-tiny text-ink-faint xl:inline-block max-w-[14ch] [body.paper_&]:text-charcoal-faint"
                title={user.email}
              >
                {user.email}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleLogout()}
                loading={isPending}
                className="!text-tiny !text-ink-muted [body.paper_&]:!text-charcoal-muted"
              >
                Log out
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

function UsagePill({
  plan,
  used,
  limit,
  onUpgrade,
}: {
  plan: 'free' | 'pro' | 'byo';
  used: number;
  limit: number;
  onUpgrade: () => void;
}) {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  // Free users get the upgrade-pressure tone (warm at 80%, danger at 100%).
  // Paid users get a neutral muted tone since the pill is informational —
  // they don't need conversion pressure on their own dashboard.
  const tone = plan === 'free'
    ? pct >= 100
      ? 'border-danger/40 bg-danger/10 text-danger'
      : pct >= 80
        ? 'border-amber-500/40 bg-amber-500/10 text-amber-300 [body.paper_&]:bg-amber-50 [body.paper_&]:text-amber-700 [body.paper_&]:border-amber-400/60'
        : 'border-brand-500/40 bg-brand-500/10 text-brand-300 [body.paper_&]:border-teal-500/40 [body.paper_&]:bg-teal-50 [body.paper_&]:text-teal-700'
    : 'border-line-default bg-clyra-raised/60 text-ink-secondary [body.paper_&]:border-stone-default [body.paper_&]:bg-paper-soft [body.paper_&]:text-charcoal-muted';
  const planLabel = plan === 'pro' ? 'Pro' : plan === 'byo' ? 'BYO' : 'Upgrade';
  const isFree = plan === 'free';
  return (
    <button
      type="button"
      onClick={isFree ? onUpgrade : undefined}
      disabled={!isFree}
      className={`flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-tiny font-medium transition-colors ${
        isFree ? 'hover:brightness-110 cursor-pointer' : 'cursor-default'
      } ${tone}`}
      title={isFree ? 'Daily generations — click to upgrade' : `Daily generations on ${planLabel}`}
    >
      <span className="tabular-nums">
        {used}/{limit}
      </span>
      <span className="text-ink-faint [body.paper_&]:text-charcoal-faint">·</span>
      <span>{planLabel}</span>
    </button>
  );
}

function NavItem({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `rounded-md px-2 py-0.5 text-tiny font-medium transition-colors ${
          isActive
            ? 'bg-clyra-raised text-ink-primary [body.paper_&]:bg-paper-soft [body.paper_&]:text-charcoal'
            : 'text-ink-muted hover:text-ink-primary hover:bg-clyra-raised/60 [body.paper_&]:text-charcoal-muted [body.paper_&]:hover:text-charcoal [body.paper_&]:hover:bg-paper-soft'
        }`
      }
      end
    >
      {children}
    </NavLink>
  );
}
